import React from 'react'
import { Tweet } from 'react-tweet'

import { sanitizeHref } from '@/utilities/security/isSafeHref'

import { extractInstagramEmbed } from './instagram'
import { InstagramFrame } from './InstagramFrame.client'
import { YouTubeFacade } from './YouTubeFacade.client'

/** Extrai o ID numérico do tweet de uma URL de x.com/twitter.com (…/status/<id>). */
function extractTweetId(url: string): string | null {
  const m = url.trim().match(/(?:twitter|x)\.com\/[^/]+\/status\/(\d+)/i)
  return m ? m[1] : null
}

export type EmbedBlockProps = {
  url?: string | null
  embedType: 'youtube' | 'twitter' | 'instagram'
  className?: string
  /**
   * Rótulo a11y do link de fallback (vídeo sem ID válido). Default pt — o conversor
   * de RichText (server/client) injecta a versão traduzida quando o módulo de idiomas está activo.
   */
  ariaLabel?: string
  /** `title` do iframe do YouTube. Default pt; traduzido via conversor de RichText. */
  iframeTitle?: string
}

function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url.trim())
    const host = u.hostname.replace(/^www\.|^m\./, '')
    if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname.startsWith('/watch')) return u.searchParams.get('v')
      if (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/shorts/'))
        return u.pathname.split('/')[2] || null
    }
    if (host === 'youtu.be') return u.pathname.slice(1).split('?')[0] || null
  } catch {
    /* ignore */
  }
  return null
}

export function EmbedBlock({
  url,
  embedType,
  className,
  ariaLabel = 'Abrir vídeo no YouTube (nova aba)',
  iframeTitle = 'Vídeo do YouTube',
}: EmbedBlockProps) {
  // url agora é opcional (ver config) — embed sem url não renderiza nada (evita crash em `url.trim()`).
  if (!url || typeof url !== 'string' || !url.trim()) return null

  /*
   * INSTAGRAM (19/08/2026). Tolerante ao tipo: se a URL é do Instagram, renderiza Instagram mesmo
   * que o editor tenha deixado «YouTube»/«Twitter» no seletor — o seletor nasceu com só dois
   * valores e o default é YouTube; errar ali não pode deixar um comunicado sem o post.
   * `/embed/captioned/` = o iframe oficial do Instagram (o que o embed.js deles cria), com legenda.
   * Altura fixa porque sem o script não há auto-resize; o conteúdo do Instagram é responsivo
   * dentro do iframe. `loading="lazy"`: só baixa quando entra na tela.
   */
  const ig = embedType === 'instagram' || /instagr/i.test(url) ? extractInstagramEmbed(url) : null
  if (ig) {
    // Altura exata vem do próprio Instagram por postMessage — ver InstagramFrame.client.tsx.
    return <InstagramFrame item={ig} className={className} />
  }
  if (embedType === 'instagram') {
    // URL que não é de post/reel (perfil, por exemplo): link simples, nunca iframe quebrado.
    return (
      <div className={className}>
        <a href={sanitizeHref(url.trim())} target="_blank" rel="noopener noreferrer">
          {url.trim()}
        </a>
      </div>
    )
  }

  if (embedType === 'youtube') {
    const videoId = extractYouTubeId(url)
    if (!videoId) {
      return (
        <div className={className}>
          <a
            href={sanitizeHref(url)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaLabel}
          >
            {url}
          </a>
        </div>
      )
    }
    // Capa leve (facade): miniatura + ▶; o player pesado do YouTube só carrega ao clicar.
    return (
      <div className={className}>
        <YouTubeFacade videoId={videoId} title={iframeTitle} />
      </div>
    )
  }

  if (embedType === 'twitter') {
    // Tweet renderizado NO SERVIDOR (react-tweet, isomórfico): vem pronto no HTML, carrega sempre
    // (inclusive navegando entre posts) e sem os 3-5s do antigo widgets.js. `react-tweet` traz o próprio
    // CSS. Sem ID válido → fallback de link. Tweet apagado/privado → o próprio react-tweet mostra fallback.
    const tweetId = extractTweetId(url)
    if (!tweetId) {
      return (
        <div className={className}>
          <a href={sanitizeHref(url.trim())} target="_blank" rel="noopener noreferrer">
            {url.trim()}
          </a>
        </div>
      )
    }
    return (
      <div
        className={['embed-block embed-block--twitter', className].filter(Boolean).join(' ')}
        // Centrado no post por padrão (pedido do dono, 19/08/2026) — vale para X e Instagram.
        style={{ maxWidth: 550, width: '100%', marginLeft: 'auto', marginRight: 'auto' }}
      >
        <Tweet id={tweetId} />
      </div>
    )
  }

  return null
}
